/* Copyright (c) 2021-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { z } from "zod";
import { CloudAccountCloudProvider } from "~/generated/graphql";
import { safeStr, stringRequired } from "~/components/Forms";
import { SvgName } from "~/tools/types";
import { isValidJSONObject } from "~/utils/json";
import {
  MAX_DISPLAY_NAME_LENGTH,
  MAX_DESCRIPTION_LENGTH,
} from "~/views/applications/constants";

const validateGoogleCloudJsonFile = (file: File) => {
  return new Promise<File>((resolve, reject) => {
    const reader = new FileReader();

    const handleLoad = (event: ProgressEvent<FileReader>) => {
      try {
        const content = (event.target?.result as string)?.trim();

        if (!content) {
          reject(new Error("JSON file is empty"));
          return;
        }

        if (!isValidJSONObject(content)) {
          reject(new Error("Invalid JSON format"));
          return;
        }

        const json = JSON.parse(content);
        if (Object.keys(json).length === 0) {
          reject(new Error("JSON must contain a non-empty object"));
          return;
        }

        resolve(file);
      } catch (error) {
        reject(
          error instanceof Error
            ? error
            : new Error("Unknown error while validating file")
        );
      } finally {
        reader.removeEventListener("load", handleLoad);
        reader.removeEventListener("error", handleError);
      }
    };

    const handleError = () => {
      reject(new Error("Failed to read file"));
      reader.removeEventListener("load", handleLoad);
      reader.removeEventListener("error", handleError);
    };

    reader.addEventListener("load", handleLoad);
    reader.addEventListener("error", handleError);

    reader.readAsText(file);
  });
};

export enum FormField {
  description = "description",
  environment = "environment",
  displayName = "displayName",
  provider = "provider",
  providerId = "providerId",
  roleExternalId = "roleExternalId",
  roleArn = "roleArn",
  subscriptionId = "subscriptionId",
  appId = "appId",
  password = "password",
  tenantId = "tenantId",
  regions = "regions",
  projectId = "projectId",
  jsonFile = "jsonFile",
  apiKey = "apiKey",
}

type Item = {
  iconName: SvgName;
  id: CloudAccountCloudProvider;
};

export const PROVIDER_CONFIG: Item[] = [
  {
    iconName: "aws",
    id: CloudAccountCloudProvider.Aws,
  },
  {
    iconName: "azure",
    id: CloudAccountCloudProvider.Azure,
  },
  {
    iconName: "gcp",
    id: CloudAccountCloudProvider.Gc,
  },
  {
    iconName: "ibmCloud",
    id: CloudAccountCloudProvider.Ibm,
  },
];

export const schemaAws = z.object({
  [FormField.displayName]: safeStr
    .max(MAX_DISPLAY_NAME_LENGTH, {
      message: `Account Name is too long (max: ${MAX_DISPLAY_NAME_LENGTH} characters)`,
    })
    .optional(),
  [FormField.description]: safeStr
    .max(MAX_DESCRIPTION_LENGTH, {
      message: `Description is too long (max: ${MAX_DESCRIPTION_LENGTH} characters)`,
    })
    .optional(),
  [FormField.environment]: safeStr
    .max(100, {
      message: "Environment is too long (max: 100 characters)",
    })
    .optional(),
  [FormField.provider]: z.nativeEnum(CloudAccountCloudProvider),
  [FormField.providerId]: stringRequired("Account Id").length(12),
  [FormField.roleExternalId]: stringRequired("External Id"),
  [FormField.roleArn]: stringRequired("Role ARN"),
  [FormField.regions]: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .min(1, { message: "This field has to be filled." }),
});

export const schemaAzure = z.object({
  [FormField.displayName]: safeStr
    .max(MAX_DISPLAY_NAME_LENGTH, {
      message: `Account Name is too long (max: ${MAX_DISPLAY_NAME_LENGTH} characters)`,
    })
    .optional(),
  [FormField.description]: safeStr
    .max(MAX_DESCRIPTION_LENGTH, {
      message: `Description is too long (max: ${MAX_DESCRIPTION_LENGTH} characters)`,
    })
    .optional(),
  [FormField.environment]: safeStr
    .max(100, {
      message: "Environment is too long (max: 100 characters)",
    })
    .optional(),
  [FormField.provider]: z.nativeEnum(CloudAccountCloudProvider),
  [FormField.subscriptionId]: stringRequired("Subscription ID"),
  [FormField.appId]: stringRequired("App ID"),
  [FormField.password]: stringRequired("Password"),
  [FormField.tenantId]: stringRequired("Tenant"),
});

export const schemaGoogle = z.object({
  [FormField.displayName]: safeStr
    .max(MAX_DISPLAY_NAME_LENGTH, {
      message: `Account Name is too long (max: ${MAX_DISPLAY_NAME_LENGTH} characters)`,
    })
    .optional(),
  [FormField.description]: safeStr
    .max(MAX_DESCRIPTION_LENGTH, {
      message: `Description is too long (max: ${MAX_DESCRIPTION_LENGTH} characters)`,
    })
    .optional(),
  [FormField.environment]: safeStr
    .max(100, {
      message: "Environment is too long (max: 100 characters)",
    })
    .optional(),
  [FormField.provider]: z.nativeEnum(CloudAccountCloudProvider),
  [FormField.projectId]: stringRequired("Project ID"),
  [FormField.jsonFile]: z
    .instanceof(File, {
      message: "JSON File is required",
    })
    .refine(
      async (file) => {
        try {
          await validateGoogleCloudJsonFile(file);
          return true;
        } catch (error) {
          return false;
        }
      },
      {
        message:
          "Invalid service account JSON file. Please upload a valid Google Cloud service account key file.",
      }
    ),
  [FormField.regions]: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .min(1, { message: "This field has to be filled." }),
});

export const schemaIbm = z.object({
  [FormField.displayName]: safeStr
    .max(MAX_DISPLAY_NAME_LENGTH, {
      message: `Account Name is too long (max: ${MAX_DISPLAY_NAME_LENGTH} characters)`,
    })
    .optional(),
  [FormField.description]: safeStr
    .max(MAX_DESCRIPTION_LENGTH, {
      message: `Description is too long (max: ${MAX_DESCRIPTION_LENGTH} characters)`,
    })
    .optional(),
  [FormField.environment]: safeStr
    .max(100, {
      message: "Environment is too long (max: 100 characters)",
    })
    .optional(),
  [FormField.provider]: z.nativeEnum(CloudAccountCloudProvider),
  [FormField.providerId]: stringRequired("IBM Cloud Account ID").regex(
    /^[0-9a-f]{32}$/,
    "IBM Cloud Account ID must contain exactly 32 lowercase hexadecimal characters."
  ),
  [FormField.apiKey]: stringRequired("API Key"),
  [FormField.regions]: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .min(1, { message: "This field has to be filled." }),
});

export type FormDataAws = {
  [FormField.displayName]: string;
  [FormField.description]?: string;
  [FormField.environment]?: string;
  [FormField.provider]: CloudAccountCloudProvider;
  [FormField.providerId]: string;
  [FormField.roleExternalId]: string;
  [FormField.roleArn]: string;
  [FormField.regions]: { label: string; value: string }[];
};

export type FormDataAzure = {
  [FormField.displayName]: string;
  [FormField.description]?: string;
  [FormField.environment]?: string;
  [FormField.provider]: CloudAccountCloudProvider;
  [FormField.subscriptionId]: string;
  [FormField.appId]: string;
  [FormField.password]: string;
  [FormField.tenantId]: string;
};

export type FormDataGoogle = {
  [FormField.displayName]: string;
  [FormField.description]?: string;
  [FormField.environment]?: string;
  [FormField.provider]: CloudAccountCloudProvider;
  [FormField.projectId]: string;
  [FormField.jsonFile]: File;
  [FormField.regions]: { label: string; value: string }[];
};

export type FormDataIbm = {
  [FormField.displayName]: string;
  [FormField.description]?: string;
  [FormField.environment]?: string;
  [FormField.provider]: CloudAccountCloudProvider;
  [FormField.providerId]: string;
  [FormField.apiKey]: string;
  [FormField.regions]: { label: string; value: string }[];
};
